/**
 * ActiveAdventureList Component
 * 
 * Displays a list of active adventure sessions for the current user.
 * Used in rich text rendering for <Active-Adventure-List> tags.
 * Items are clickable and will navigate to the existing session when clicked (not create new).
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveAdventures } from '../../hooks/useActiveAdventures';
import { AdventureTitleImage } from './AdventureTitleImage';
import apiService from '../../services/api';
import useGameStore from '../../stores/gameStore';

function parseFilterTags(tags?: string): string[] {
  if (!tags || !tags.trim()) {
    return [];
  }
  return tags
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

function adventureMatchesAnyTag(adventureTags: string[] | undefined, filterTags: string[]): boolean {
  if (filterTags.length === 0) {
    return true;
  }
  if (!adventureTags || adventureTags.length === 0) {
    return false;
  }
  const tagSet = new Set(adventureTags);
  return filterTags.some((tag) => tagSet.has(tag));
}

export interface ActiveAdventureListProps {
  /**
   * Whether to exclude system adventures (e.g., 'storynity')
   */
  excludeSystemAdventures?: boolean;

  /**
   * Optional comma-separated adventure tags; list shows active sessions whose
   * adventure matches any listed tag.
   */
  tags?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
  
  /**
   * Optional custom button color for styling adventure buttons
   */
  customColor?: string;
}

/**
 * ActiveAdventureList component for displaying lists of active adventures in rich text
 */
export const ActiveAdventureList: React.FC<ActiveAdventureListProps> = ({
  excludeSystemAdventures = true,
  tags,
  className = '',
  style,
  customColor
}) => {
  const navigate = useNavigate();
  const systemAdventureId = useGameStore((s) => s.systemAdventureId);
  const { adventures, loading, error } = useActiveAdventures();
  const [navigatingSessionId, setNavigatingSessionId] = React.useState<string | null>(null);
  const [titleImageIds, setTitleImageIds] = React.useState<Map<string, string>>(new Map());
  const [adventureTagsById, setAdventureTagsById] = React.useState<Map<string, string[]>>(new Map());
  const [headersLoaded, setHeadersLoaded] = React.useState(false);
  const filterTags = useMemo(() => parseFilterTags(tags), [tags]);

  // Fetch title image IDs and tags for all adventures
  React.useEffect(() => {
    if (adventures.length === 0) {
      setHeadersLoaded(true);
      return;
    }

    let cancelled = false;
    setHeadersLoaded(false);

    const fetchHeaders = async () => {
      const imageMap = new Map<string, string>();
      const tagsMap = new Map<string, string[]>();
      
      await Promise.all(
        adventures.map(async (adventure) => {
          try {
            const header = await apiService.getAdventureHeader(adventure.session.adventureId);
            if (header.titleImageId != null) {
              imageMap.set(adventure.session.id, header.titleImageId);
            }
            tagsMap.set(adventure.session.adventureId, header.tags ?? []);
          } catch (err) {
            console.warn(`Failed to load adventure header for ${adventure.session.adventureId}:`, err);
          }
        })
      );

      if (!cancelled) {
        setTitleImageIds(imageMap);
        setAdventureTagsById(tagsMap);
        setHeadersLoaded(true);
      }
    };

    fetchHeaders();

    return () => {
      cancelled = true;
    };
  }, [adventures]);

  const handleAdventureClick = (sessionId: string, playerId: string | undefined) => {
    if (!playerId || navigatingSessionId) return;
    
    setNavigatingSessionId(sessionId);
    // Navigate to existing session (not create new)
    navigate(`/scene/${sessionId}/${playerId}`);
  };

  const filteredAdventures = useMemo(() => {
    let result = excludeSystemAdventures
      ? adventures.filter((adventure) => adventure.session.adventureId !== systemAdventureId)
      : adventures;

    if (filterTags.length === 0) {
      return result;
    }

    return result.filter((adventure) =>
      adventureMatchesAnyTag(adventureTagsById.get(adventure.session.adventureId), filterTags)
    );
  }, [adventures, excludeSystemAdventures, systemAdventureId, filterTags, adventureTagsById]);

  if (loading || (filterTags.length > 0 && !headersLoaded)) {
    return (
      <div 
        className={`active-adventure-list loading ${className}`} 
        style={style}
        data-testid="active-adventure-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">Loading active adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`active-adventure-list error ${className}`} 
        style={style}
        data-testid="active-adventure-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400">Error loading active adventures: {error}</p>
        </div>
      </div>
    );
  }

  if (filteredAdventures.length === 0) {
    return (
      <div 
        className={`active-adventure-list empty ${className}`} 
        style={style}
        data-testid="active-adventure-list-empty"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">No active adventures</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`active-adventure-list ${className}`} 
      style={style}
      data-testid="active-adventure-list"
    >
      <div
        className="active-adventure-list-items flex flex-wrap justify-center"
        style={{ gap: '1rem', rowGap: '0.5rem' }}
        data-testid="active-adventure-items-list"
      >
        {filteredAdventures.map((adventure) => {
          const isNavigating = navigatingSessionId === adventure.session.id;
          const titleImageId = titleImageIds.get(adventure.session.id);
          
          // Apply custom color styling if provided
          const getColorWithAlpha = (color: string, alpha: string) => {
            if (color.startsWith('#')) {
              return `${color}${alpha}`;
            }
            return color;
          };
          
          const buttonStyle: React.CSSProperties = { 
            width: '512px', 
            maxWidth: '100%', 
            marginBottom: '0.5rem' 
          };
          
          if (customColor && !isNavigating && adventure.playerId) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }
          
          return (
            <button
              key={adventure.session.id}
              onClick={() => handleAdventureClick(adventure.session.id, adventure.playerId)}
              disabled={isNavigating || !adventure.playerId}
              className="active-adventure-item-button adventure-item-button inventory-item-button"
              style={buttonStyle}
              data-testid={`active-adventure-item-${adventure.session.id}`}
              data-session-id={adventure.session.id}
              aria-label={`Resume ${adventure.session.adventureId}`}
            >
              <div className="active-adventure-item-image" style={{ width: '100%' }}>
                {isNavigating ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-gray-400 italic">Resuming...</span>
                  </div>
                ) : titleImageId ? (
                  <AdventureTitleImage
                    titleImageId={titleImageId}
                    adventureId={adventure.session.adventureId}
                    variant="card"
                    alt={`Resume ${adventure.session.adventureId}`}
                    responsive={true}
                  />
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-gray-400 italic">Loading image...</span>
                  </div>
                )}
              </div>
              {adventure.characterName && (
                <div className="text-center mt-2 text-sm text-gray-300">
                  {adventure.characterName}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveAdventureList;
